import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search, SwitchCamera, Pencil, Trash2, ChevronRight, Check, Loader2, Star } from 'lucide-react';
import { clsx } from 'clsx';
import api from '@/api';
import { getClientActiveAid } from '@/api';
import { useAuth } from '@/auth/AuthContext';
import { useAccountRole } from '@/hooks/useAccountRole';
import {
  setDefaultWorkspaceAccount,
  switchAndSetDefaultWorkspaceAccount,
  switchWorkspaceAccount,
} from '@/utils/notifyAccountSwitch';
import { AccountModal } from '@/components/modals/AccountModal';
import { DeleteAccountModal } from '@/components/modals/DeleteAccountModal';

type WorkspaceAccount = {
  id: string;
  name: string;
  adminId: string;
  createdAt: number;
  plans: number;
  users: { uid: string; status: boolean }[];
  isUsingHere?: boolean;
  isDefault?: boolean;
};

const AccountCard = ({
  account,
  onSwitch,
  onSetDefault,
  onEdit,
  onDelete,
  switching,
  settingDefault,
  canDelete,
  canEdit,
}: {
  account: WorkspaceAccount;
  onSwitch: (id: string) => void;
  onSetDefault: (id: string) => void;
  onEdit: (account: WorkspaceAccount) => void;
  onDelete: (account: WorkspaceAccount) => void;
  switching: boolean;
  settingDefault: boolean;
  canDelete: boolean;
  canEdit: boolean;
}) => {
  const { t } = useTranslation('settings');
  const accountName = account?.name || 'Unnamed Account';
  const initials = accountName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase() || '?';

  const colors = ['bg-blue-600', 'bg-emerald-600', 'bg-purple-600', 'bg-orange-600', 'bg-pink-600'];
  const avatarColor = colors[accountName.length % colors.length];
  const busy = switching || settingDefault;

  return (
    <div
      className={clsx(
        'group relative flex flex-col gap-3 rounded-2xl border-2 bg-card p-4 transition-all sm:gap-5 sm:rounded-3xl sm:p-6 md:gap-6',
        account.isUsingHere
          ? 'border-primary ring-2 ring-blue-500/5'
          : 'border-border hover:border-foreground/20'
      )}
    >
      <div className="flex gap-3">
        <div
          className={clsx(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xs font-bold text-white sm:h-12 sm:w-12 sm:rounded-2xl sm:text-sm',
            avatarColor
          )}
        >
          {initials}
        </div>
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex items-start justify-between gap-2">
            <h4 className="min-w-0 truncate text-base font-bold text-foreground transition-colors group-hover:text-primary sm:text-lg">
              {accountName}
            </h4>
            <div
              className={clsx(
                'shrink-0 rounded-md px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider sm:rounded-lg sm:px-2.5 sm:py-1 sm:text-[10px]',
                (account?.plans ?? 0) > 0
                  ? 'bg-purple-500/10 text-purple-400'
                  : 'bg-blue-500/10 text-blue-400'
              )}
            >
              {(account?.plans ?? 0) > 0 ? t('companyAccount.enterprise') : t('companyAccount.personal')}
            </div>
          </div>
          <p className="truncate text-[11px] font-medium text-muted-foreground sm:text-xs">
            {t('companyAccount.members', { count: account?.users?.length ?? 0 })}
          </p>
        </div>
      </div>

      <div className="h-px w-full bg-border" />

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => !account.isUsingHere && !busy && onSwitch(account.id)}
            disabled={account.isUsingHere || busy}
            className={clsx(
              'flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-bold transition-all sm:min-h-0 sm:gap-2 sm:py-2.5',
              account.isUsingHere || busy
                ? 'cursor-default bg-muted text-muted-foreground'
                : 'bg-primary/10 text-primary hover:bg-primary hover:text-white'
            )}
          >
            {account.isUsingHere ? (
              <>
                <Check size={14} />
                {t('companyAccount.current')}
              </>
            ) : switching ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                {t('companyAccount.switching')}
              </>
            ) : (
              <>
                <SwitchCamera size={14} />
                {t('companyAccount.useOnThisDevice')}
              </>
            )}
          </button>

          {canEdit ? (
          <button
            type="button"
            onClick={() => onEdit(account)}
            className="rounded-lg p-2 text-muted-foreground transition-all hover:bg-muted hover:text-foreground sm:rounded-xl sm:p-2.5"
            aria-label={t('companyAccount.editAria')}
          >
            <Pencil size={16} />
          </button>
          ) : null}
          {canDelete && (
            <button
              type="button"
              onClick={() => onDelete(account)}
              className="rounded-lg p-2 text-muted-foreground transition-all hover:bg-red-500/5 hover:text-red-500 sm:rounded-xl sm:p-2.5"
              aria-label={t('companyAccount.deleteAria')}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
        <div className="flex min-h-5 items-center">
          {account.isDefault ? (
            <span
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary sm:text-xs"
              title={t('companyAccount.default')}
            >
              <Star size={14} className="shrink-0 fill-primary/25 text-primary" aria-hidden />
              {t('companyAccount.default')}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => !busy && onSetDefault(account.id)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-left text-[11px] font-semibold text-muted-foreground underline-offset-2 hover:text-primary hover:underline disabled:opacity-50 sm:text-xs"
            >
              {settingDefault ? (
                <>
                  <Loader2 size={13} className="animate-spin" aria-hidden />
                  {t('companyAccount.settingDefault')}
                </>
              ) : (
                <>
                  <Star size={13} className="shrink-0 opacity-60" aria-hidden />
                  {t('companyAccount.setAsDefault')}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const AddAccountCard = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation('settings');
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex min-h-[160px] flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-border p-5 transition-all hover:border-primary/50 hover:bg-primary/[0.02] sm:min-h-[220px] sm:gap-4 sm:rounded-3xl sm:p-8"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground transition-all group-hover:bg-primary group-hover:text-white sm:h-12 sm:w-12">
        <Plus size={22} className="sm:h-6 sm:w-6" />
      </div>
      <div className="text-center">
        <span className="block text-xs font-bold text-muted-foreground transition-colors group-hover:text-foreground sm:text-sm">
          {t('companyAccount.addAnotherAccount')}
        </span>
        <span className="mt-1 block text-[10px] font-medium italic leading-snug text-muted-foreground/60 sm:text-[11px]">
          {t('companyAccount.addAnotherHint')}
        </span>
      </div>
    </button>
  );
};

export const CompanyAccountSettings = () => {
  const { t } = useTranslation('settings');
  const { refreshUserPreference, user } = useAuth();
  const { canManageAccount, isWorkspaceOwner } = useAccountRole();
  const [accounts, setAccounts] = useState<WorkspaceAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [switchingAid, setSwitchingAid] = useState<string | null>(null);
  const [settingDefaultAid, setSettingDefaultAid] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pendingSwitchAid, setPendingSwitchAid] = useState<string | null>(null);
  const [alsoSetAsDefault, setAlsoSetAsDefault] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingAccount, setEditingAccount] = useState<WorkspaceAccount | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<WorkspaceAccount | null>(null);

  const mapAccounts = useCallback(
    (fetched: WorkspaceAccount[], defaultAid: string | null) => {
      const localAid = getClientActiveAid() ?? user?.aid ?? null;
      return fetched.map((acc) => ({
        ...acc,
        isUsingHere: acc.id === localAid,
        isDefault: acc.id === defaultAid,
      }));
    },
    [user?.aid]
  );

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<{
        status: string;
        accounts: WorkspaceAccount[];
        defaultAid?: string;
        aid?: string;
      }>('/users/accounts');
      if (response.data.status === 'success') {
        const defaultAid =
          response.data.defaultAid ?? response.data.aid ?? null;
        setAccounts(mapAccounts(response.data.accounts, defaultAid));
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch accounts';
      setError(errorMessage);
      console.error('Fetch accounts error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  const handleSwitchConfirm = async () => {
    if (!pendingSwitchAid) return;
    const aid = pendingSwitchAid;
    setPendingSwitchAid(null);
    try {
      setSwitchingAid(aid);
      setError(null);
      if (alsoSetAsDefault) {
        await switchAndSetDefaultWorkspaceAccount(aid, { refreshUserPreference });
      } else {
        await switchWorkspaceAccount(aid, { refreshUserPreference });
      }
      await fetchAccounts();
      setAlsoSetAsDefault(false);
    } catch (err) {
      console.error('Switch account error:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Could not switch workspace. Try reloading the extension or opening Trove again.'
      );
    } finally {
      setSwitchingAid(null);
    }
  };

  const handleSetDefault = async (aid: string) => {
    try {
      setSettingDefaultAid(aid);
      setError(null);
      await setDefaultWorkspaceAccount(aid);
      setAccounts((prev) =>
        prev.map((acc) => ({
          ...acc,
          isDefault: acc.id === aid,
        }))
      );
    } catch (err) {
      console.error('Set default account error:', err);
      setError(err instanceof Error ? err.message : 'Could not set default workspace');
    } finally {
      setSettingDefaultAid(null);
    }
  };

  const usingHereName = accounts.find((a) => a.isUsingHere)?.name;
  const defaultName = accounts.find((a) => a.isDefault)?.name;
  const showDivergentHint =
    usingHereName && defaultName && usingHereName !== defaultName;

  return (
    <div className="animate-in space-y-5 fade-in slide-in-from-bottom-2 duration-300 sm:space-y-8 lg:space-y-10">
      <nav className="flex flex-wrap items-center gap-x-2 gap-y-1 px-0.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60 sm:text-[11px]">
        <span className="shrink-0">{t('companyAccount.breadcrumbSettings')}</span>
        <ChevronRight size={10} className="shrink-0 text-muted-foreground/40" />
        <span className="min-w-0 text-muted-foreground">{t('companyAccount.breadcrumb')}</span>
      </nav>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div className="min-w-0 max-w-xl">
          <h2 className="text-2xl font-black tracking-tight text-foreground sm:text-3xl md:text-4xl">
            {t('companyAccount.title')}
          </h2>
          <p className="mt-2 text-xs leading-snug text-muted-foreground sm:mt-3 sm:text-sm sm:leading-relaxed">
            {t('companyAccount.description')}
          </p>
          {showDivergentHint && (
            <p className="mt-2 text-xs text-muted-foreground sm:text-sm">
              {t('companyAccount.divergentHint', {
                usingName: usingHereName,
                defaultName,
              })}
            </p>
          )}
        </div>
        {canManageAccount ? (
        <button
          type="button"
          onClick={() => {
            setModalMode('create');
            setEditingAccount(null);
            setIsModalOpen(true);
          }}
          className="flex min-h-11 w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 sm:min-h-0 sm:w-auto sm:rounded-2xl sm:px-6 sm:py-3"
        >
          <Plus size={18} />
          {t('companyAccount.newAccount')}
        </button>
        ) : null}
      </div>

      <div className="group relative flex-1">
        <Search
          className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground sm:left-4"
          aria-hidden
        />
        <input
          type="search"
          placeholder={t('companyAccount.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-xl border border-border bg-muted/30 py-2.5 pl-10 pr-3 text-xs font-medium sm:rounded-2xl sm:py-3 sm:pl-12 sm:text-sm"
        />
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-14 text-muted-foreground sm:py-20">
          <Loader2 className="h-9 w-9 animate-spin sm:h-10 sm:w-10" />
          <p className="text-sm font-medium">{t('companyAccount.loading')}</p>
        </div>
      ) : error ? (
        <div className="py-14 text-center sm:py-20">
          <p className="font-bold text-red-400">{error}</p>
          <button type="button" onClick={() => void fetchAccounts()} className="mt-4 font-black text-primary hover:underline">
            {t('companyAccount.tryAgain')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 pb-8 sm:gap-6 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
          {accounts
            .filter((acc) => (acc?.name || '').toLowerCase().includes(search.toLowerCase()))
            .map((acc) => (
              <AccountCard
                key={acc.id}
                account={acc}
                onSwitch={(id) => {
                  setAlsoSetAsDefault(false);
                  setPendingSwitchAid(id);
                }}
                onSetDefault={handleSetDefault}
                onEdit={(account) => {
                  setModalMode('edit');
                  setEditingAccount(account);
                  setIsModalOpen(true);
                }}
                onDelete={(account) => {
                  setAccountToDelete(account);
                  setIsDeleteModalOpen(true);
                }}
                switching={switchingAid === acc.id}
                settingDefault={settingDefaultAid === acc.id}
                canDelete={isWorkspaceOwner && acc.adminId === user?.id}
                canEdit={canManageAccount}
              />
            ))}
          {canManageAccount ? (
          <AddAccountCard onClick={() => {
            setModalMode('create');
            setEditingAccount(null);
            setIsModalOpen(true);
          }} />
          ) : null}
        </div>
      )}

      {pendingSwitchAid && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl">
            <h3 className="text-lg font-bold text-foreground">{t('companyAccount.switchConfirmTitle')}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{t('companyAccount.switchConfirmBody')}</p>
            <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={alsoSetAsDefault}
                onChange={(e) => setAlsoSetAsDefault(e.target.checked)}
                className="mt-0.5"
              />
              <span>{t('companyAccount.alsoSetAsDefault')}</span>
            </label>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg px-4 py-2 text-sm font-semibold text-muted-foreground hover:bg-muted"
                onClick={() => {
                  setPendingSwitchAid(null);
                  setAlsoSetAsDefault(false);
                }}
              >
                {t('companyAccount.switchConfirmCancel')}
              </button>
              <button
                type="button"
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                onClick={() => void handleSwitchConfirm()}
              >
                {t('companyAccount.switchConfirmAction')}
              </button>
            </div>
          </div>
        </div>
      )}

      <AccountModal
        key={modalMode === 'edit' ? `edit-${editingAccount?.id ?? 'unknown'}` : 'create'}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingAccount(null);
          setModalMode('create');
        }}
        onSave={(name) => {
          if (modalMode === 'create') {
            void api.post('/users/accounts', { name }).then(() => fetchAccounts());
          } else if (editingAccount) {
            void api.put('/users/accounts', { id: editingAccount.id, name }).then(() => fetchAccounts());
          }
        }}
        initialName={editingAccount?.name ?? ''}
        title={modalMode === 'create' ? t('companyAccount.createTitle') : t('companyAccount.editTitle')}
        isEditMode={modalMode === 'edit'}
      />

      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setAccountToDelete(null);
        }}
        accountId={accountToDelete?.id || ''}
        accountName={accountToDelete?.name || ''}
        wasActiveAccount={accountToDelete?.isUsingHere === true}
        memberCount={accountToDelete?.users?.length ?? 1}
        isOnlyWorkspace={accounts.length === 1}
        onSuccess={(id) => setAccounts((prev) => prev.filter((acc) => acc.id !== id))}
      />
    </div>
  );
};
